import { apiClient } from '../../services/apiClient';
import type { PublicBadgeSummary } from '../badges/badgeApi';

/** Current competition-mode state for a course (US7, FR-043). */
export interface CompetitionState {
  active: boolean;
  chapterId?: string;
  startedAt?: string;
  endedAt?: string;
}

/**
 * The caller's single most-relevant active challenge across all their courses, used to resolve
 * nav-link targets without requiring the caller to already know a course ID (FR-001/FR-002/FR-006).
 */
export interface ActiveChallenge {
  active: boolean;
  courseId: string | null;
  chapterId: string | null;
  startedAt: string | null;
}

export interface ActivatedCompetitionSession {
  competitionSessionId: string;
  startedAt: string;
}

/** One exercise of a live challenge, flagged if the caller passed it since the challenge started. */
export interface ChallengeExercise {
  id: string;
  title: string;
  type: string;
  points: number;
  passedInChallenge: boolean;
}

/** The live challenge's chapter and its exercises, in order (student "start/continue the challenge"). */
export interface ChallengeExercises {
  chapterId: string;
  chapterTitle: string;
  exercises: ChallengeExercise[];
}

/** A single ranked row on the instructor leaderboard (US7, FR-044 to FR-049). */
export interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  totalPoints: number;
  displayBadge?: PublicBadgeSummary | null;
}

export function getCompetitionState(courseId: string): Promise<CompetitionState> {
  return apiClient(`/courses/${courseId}/competition`);
}

/**
 * Resolves the caller's single most-relevant active challenge across all their courses (US1,
 * FR-001/FR-002/FR-006). Polled by `MenuShell` every ~3s to drive the "Challenge" nav link target
 * and the blinking live indicator.
 */
export function getActiveChallenge(): Promise<ActiveChallenge> {
  return apiClient('/me/active-challenge');
}

export function activateCompetition(
  courseId: string,
  chapterId?: string,
): Promise<ActivatedCompetitionSession> {
  return apiClient(`/courses/${courseId}/competition`, {
    method: 'POST',
    body: JSON.stringify({ chapterId: chapterId ?? null }),
  });
}

/** Restarts the active challenge on the same chapter; standings start again at zero. */
export function resetCompetition(courseId: string): Promise<ActivatedCompetitionSession> {
  return apiClient(`/courses/${courseId}/competition/reset`, { method: 'POST' });
}

export function deactivateCompetition(courseId: string): Promise<void> {
  return apiClient(`/courses/${courseId}/competition`, { method: 'DELETE' });
}

/** Exercises of the course's live challenge; rejects (404) when no challenge is running. */
export function getChallengeExercises(courseId: string): Promise<ChallengeExercises> {
  return apiClient(`/courses/${courseId}/competition/exercises`);
}

/** Polled every ~3s by the leaderboard page while it is open (research.md §10). */
export function getLeaderboard(courseId: string): Promise<LeaderboardEntry[]> {
  return apiClient(`/courses/${courseId}/leaderboard`);
}

/** The caller's saved display alias; `alias` is null when none has been set yet. */
export interface MyAlias {
  alias: string | null;
}

/** Reads the authenticated student's own display alias (US7, FR-049). */
export function getMyAlias(): Promise<MyAlias> {
  return apiClient('/me/alias');
}

/** Sets the authenticated student's own display alias (US7, FR-049, Law 25 privacy). */
export function setMyAlias(alias: string): Promise<void> {
  return apiClient('/me/alias', {
    method: 'PUT',
    body: JSON.stringify({ alias }),
  });
}
